/**
 * Theme System
 *
 * This module defines the structure for themes and provides
 * preset themes that users can select from, similar to Slack.
 */

// Theme color values should be in RGB format (e.g., "255 255 255" for white)
// This matches our CSS variable format which allows for opacity support
export interface ThemeColors {
  // Background colors
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;

  // Foreground/text colors
  foreground: string;
  foregroundSecondary: string;
  foregroundTertiary: string;
  foregroundMuted: string;

  // Border colors
  border: string;
  borderSecondary: string;

  // Primary brand color
  primary: string;
  primaryForeground: string;

  // Accent colors
  accent: string;
  accentForeground: string;

  // Card/surface colors
  card: string;
  cardForeground: string;

  // Muted colors
  muted: string;
  mutedForeground: string;

  // Sidebar colors
  sidebarBackground: string;
  sidebarForeground: string;
  sidebarBorder: string;
  sidebarAccent: string;

  // Interactive states
  hover: string;
  hoverForeground: string;
  rowHover: string;

  // Table colors
  tableHeader: string;
  tableHeaderForeground: string;
  tableRowHover: string;
  tableBorder: string;

  // Link colors
  link: string;
  linkHover: string;

  // Input/form colors
  input: string;
  ring: string;

  // Destructive colors
  destructive: string;
  destructiveForeground: string;
}

export interface CustomTheme {
  id: string;
  name: string;
  baseMode: "light" | "dark"; // Determines scrollbars, form fields, etc.
  colors: ThemeColors;
  isBuiltIn?: boolean;
  hidden?: boolean; // Hidden themes appear under "see more"
}

// Default light mode colors
const defaultLightColors: ThemeColors = {
  background: "250 246 240",
  backgroundSecondary: "244 238 228",
  backgroundTertiary: "235 225 210",
  foreground: "27 33 27",
  foregroundSecondary: "74 93 78",
  foregroundTertiary: "130 148 134",
  foregroundMuted: "164 150 132",
  border: "225 215 200",
  borderSecondary: "197 163 126",
  primary: "74 93 78",
  primaryForeground: "250 246 240",
  accent: "235 225 210",
  accentForeground: "27 33 27",
  card: "250 246 240",
  cardForeground: "27 33 27",
  muted: "235 225 210",
  mutedForeground: "164 150 132",
  sidebarBackground: "244 238 228",
  sidebarForeground: "27 33 27",
  sidebarBorder: "225 215 200",
  sidebarAccent: "235 225 210",
  hover: "235 225 210",
  hoverForeground: "27 33 27",
  rowHover: "244 238 228",
  tableHeader: "244 238 228",
  tableHeaderForeground: "164 150 132",
  tableRowHover: "244 238 228",
  tableBorder: "225 215 200",
  link: "74 93 78",
  linkHover: "52 66 55",
  input: "225 215 200",
  ring: "74 93 78",
  destructive: "239 68 68",
  destructiveForeground: "255 255 255",
};

// Default dark mode colors
const defaultDarkColors: ThemeColors = {
  background: "27 33 27",
  backgroundSecondary: "38 46 38",
  backgroundTertiary: "52 62 52",
  foreground: "235 228 218",
  foregroundSecondary: "200 190 178",
  foregroundTertiary: "164 150 132",
  foregroundMuted: "164 150 132",
  border: "52 62 52",
  borderSecondary: "74 93 78",
  primary: "197 163 126",
  primaryForeground: "27 33 27",
  accent: "52 62 52",
  accentForeground: "250 246 240",
  card: "38 46 38",
  cardForeground: "250 246 240",
  muted: "52 62 52",
  mutedForeground: "164 150 132",
  sidebarBackground: "27 33 27",
  sidebarForeground: "250 246 240",
  sidebarBorder: "52 62 52",
  sidebarAccent: "38 46 38",
  hover: "52 62 52",
  hoverForeground: "250 246 240",
  rowHover: "38 46 38",
  tableHeader: "38 46 38",
  tableHeaderForeground: "164 150 132",
  tableRowHover: "38 46 38",
  tableBorder: "52 62 52",
  link: "197 163 126",
  linkHover: "220 190 155",
  input: "52 62 52",
  ring: "197 163 126",
  destructive: "248 113 113",
  destructiveForeground: "27 33 27",
};

// Classic light mode colors (original default)
const classicLightColors: ThemeColors = {
  background: "255 255 255",
  backgroundSecondary: "249 250 251",
  backgroundTertiary: "243 244 246",
  foreground: "17 24 39",
  foregroundSecondary: "75 85 99",
  foregroundTertiary: "156 163 175",
  foregroundMuted: "107 114 128",
  border: "229 231 235",
  borderSecondary: "209 213 219",
  primary: "1 120 212",
  primaryForeground: "255 255 255",
  accent: "124 58 237",
  accentForeground: "255 255 255",
  card: "255 255 255",
  cardForeground: "17 24 39",
  muted: "243 244 246",
  mutedForeground: "107 114 128",
  sidebarBackground: "255 255 255",
  sidebarForeground: "17 24 39",
  sidebarBorder: "229 231 235",
  sidebarAccent: "243 244 246",
  hover: "243 244 246",
  hoverForeground: "17 24 39",
  rowHover: "249 250 251",
  tableHeader: "249 250 251",
  tableHeaderForeground: "107 114 128",
  tableRowHover: "249 250 251",
  tableBorder: "229 231 235",
  link: "1 120 212",
  linkHover: "1 92 170",
  input: "229 231 235",
  ring: "1 120 212",
  destructive: "239 68 68",
  destructiveForeground: "255 255 255",
};

// Classic dark mode colors (original default)
const classicDarkColors: ThemeColors = {
  background: "17 24 39",
  backgroundSecondary: "31 41 55",
  backgroundTertiary: "55 65 81",
  foreground: "249 250 251",
  foregroundSecondary: "209 213 219",
  foregroundTertiary: "156 163 175",
  foregroundMuted: "156 163 175",
  border: "75 85 99",
  borderSecondary: "107 114 128",
  primary: "56 178 255",
  primaryForeground: "17 24 39",
  accent: "167 139 250",
  accentForeground: "17 24 39",
  card: "31 41 55",
  cardForeground: "249 250 251",
  muted: "55 65 81",
  mutedForeground: "156 163 175",
  sidebarBackground: "17 24 39",
  sidebarForeground: "249 250 251",
  sidebarBorder: "55 65 81",
  sidebarAccent: "31 41 55",
  hover: "55 65 81",
  hoverForeground: "249 250 251",
  rowHover: "31 41 55",
  tableHeader: "31 41 55",
  tableHeaderForeground: "156 163 175",
  tableRowHover: "31 41 55",
  tableBorder: "55 65 81",
  link: "56 178 255",
  linkHover: "124 203 255",
  input: "55 65 81",
  ring: "56 178 255",
  destructive: "248 113 113",
  destructiveForeground: "17 24 39",
};

// Preset themes - colorful/friendly themes first, developer themes hidden
export const presetThemes: CustomTheme[] = [
  // === Default themes (used by Standard Themes) ===
  {
    id: "default-light",
    name: "Light",
    baseMode: "light",
    colors: defaultLightColors,
    isBuiltIn: true,
  },
  {
    id: "default-dark",
    name: "Dark",
    baseMode: "dark",
    colors: defaultDarkColors,
    isBuiltIn: true,
  },
  // === Classic themes (previous defaults) ===
  {
    id: "classic-light",
    name: "Classic Light",
    baseMode: "light",
    colors: classicLightColors,
    isBuiltIn: true,
  },
  {
    id: "classic-dark",
    name: "Classic Dark",
    baseMode: "dark",
    colors: classicDarkColors,
    isBuiltIn: true,
  },

  // === Colorful themes (visible by default) ===
  {
    id: "lavender",
    name: "Lavender",
    baseMode: "light",
    colors: {
      background: "250 245 255", // soft purple white
      backgroundSecondary: "243 232 255", // violet-100
      backgroundTertiary: "233 213 255", // violet-200
      foreground: "46 16 101", // violet-950
      foregroundSecondary: "91 33 182", // violet-700
      foregroundTertiary: "124 58 237", // violet-600
      foregroundMuted: "139 92 246", // violet-500
      border: "221 214 254", // violet-200
      borderSecondary: "196 181 253", // violet-300
      primary: "124 58 237", // violet-600
      primaryForeground: "255 255 255",
      accent: "233 213 255",
      accentForeground: "46 16 101",
      card: "250 245 255",
      cardForeground: "46 16 101",
      sidebarBackground: "243 232 255",
      sidebarForeground: "46 16 101",
      sidebarBorder: "221 214 254",
      sidebarAccent: "233 213 255",
      hover: "233 213 255",
      hoverForeground: "46 16 101",
      rowHover: "243 232 255",
      muted: "233 213 255",
      mutedForeground: "139 92 246",
      tableHeader: "243 232 255",
      tableHeaderForeground: "139 92 246",
      tableRowHover: "243 232 255",
      tableBorder: "221 214 254",
      link: "124 58 237",
      linkHover: "124 58 237",
      input: "221 214 254",
      ring: "124 58 237",
      destructive: "239 68 68",
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
  },
  {
    id: "gothic-red",
    name: "Gothic Red",
    baseMode: "dark",
    colors: {
      background: "18 10 12", // deep black with red undertone
      backgroundSecondary: "32 18 22", // dark burgundy
      backgroundTertiary: "48 28 35", // lighter burgundy
      foreground: "250 245 245", // warm white
      foregroundSecondary: "220 200 205", // muted rose
      foregroundTertiary: "180 160 165", // darker rose
      foregroundMuted: "180 160 165",
      border: "60 35 42",
      borderSecondary: "80 50 58",
      primary: "180 30 50", // deep crimson
      primaryForeground: "250 245 245",
      accent: "48 28 35",
      accentForeground: "250 245 245",
      card: "32 18 22",
      cardForeground: "250 245 245",
      sidebarBackground: "18 10 12",
      sidebarForeground: "250 245 245",
      sidebarBorder: "60 35 42",
      sidebarAccent: "32 18 22",
      hover: "48 28 35",
      hoverForeground: "250 245 245",
      rowHover: "32 18 22",
      muted: "48 28 35",
      mutedForeground: "180 160 165",
      tableHeader: "32 18 22",
      tableHeaderForeground: "180 160 165",
      tableRowHover: "32 18 22",
      tableBorder: "60 35 42",
      link: "180 30 50",
      linkHover: "180 30 50",
      input: "60 35 42",
      ring: "180 30 50",
      destructive: "248 113 113",
      destructiveForeground: "18 10 12",
    },
    isBuiltIn: true,
  },
  {
    id: "rose",
    name: "Rose",
    baseMode: "light",
    colors: {
      background: "255 251 253", // rose-tinted white
      backgroundSecondary: "255 241 242", // rose-50
      backgroundTertiary: "254 205 211", // rose-200
      foreground: "76 5 25", // rose-950
      foregroundSecondary: "136 19 55", // rose-800
      foregroundTertiary: "190 18 60", // rose-700
      foregroundMuted: "190 18 60",
      border: "253 164 175", // rose-300
      borderSecondary: "251 113 133", // rose-400
      primary: "225 29 72", // rose-600
      primaryForeground: "255 255 255",
      accent: "254 205 211",
      accentForeground: "76 5 25",
      card: "255 251 253",
      cardForeground: "76 5 25",
      sidebarBackground: "255 241 242",
      sidebarForeground: "76 5 25",
      sidebarBorder: "253 164 175",
      sidebarAccent: "254 205 211",
      hover: "254 205 211",
      hoverForeground: "76 5 25",
      rowHover: "255 241 242",
      muted: "254 205 211",
      mutedForeground: "190 18 60",
      tableHeader: "255 241 242",
      tableHeaderForeground: "190 18 60",
      tableRowHover: "255 241 242",
      tableBorder: "253 164 175",
      link: "225 29 72",
      linkHover: "225 29 72",
      input: "253 164 175",
      ring: "225 29 72",
      destructive: "239 68 68",
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
  },
  {
    id: "mint",
    name: "Mint",
    baseMode: "light",
    colors: {
      background: "245 255 250", // mint white
      backgroundSecondary: "209 250 229", // emerald-100
      backgroundTertiary: "167 243 208", // emerald-200
      foreground: "6 78 59", // emerald-900
      foregroundSecondary: "4 120 87", // emerald-700
      foregroundTertiary: "16 185 129", // emerald-500
      foregroundMuted: "52 211 153", // emerald-400
      border: "167 243 208", // emerald-200
      borderSecondary: "110 231 183", // emerald-300
      primary: "16 185 129", // emerald-500
      primaryForeground: "255 255 255",
      accent: "167 243 208",
      accentForeground: "6 78 59",
      card: "245 255 250",
      cardForeground: "6 78 59",
      sidebarBackground: "209 250 229",
      sidebarForeground: "6 78 59",
      sidebarBorder: "167 243 208",
      sidebarAccent: "167 243 208",
      hover: "167 243 208",
      hoverForeground: "6 78 59",
      rowHover: "209 250 229",
      muted: "167 243 208",
      mutedForeground: "52 211 153",
      tableHeader: "209 250 229",
      tableHeaderForeground: "52 211 153",
      tableRowHover: "209 250 229",
      tableBorder: "167 243 208",
      link: "16 185 129",
      linkHover: "16 185 129",
      input: "167 243 208",
      ring: "16 185 129",
      destructive: "239 68 68",
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
  },
  {
    id: "berry",
    name: "Berry",
    baseMode: "dark",
    colors: {
      background: "59 7 100", // fuchsia-950
      backgroundSecondary: "86 10 140", // custom purple
      backgroundTertiary: "112 26 166", // custom purple
      foreground: "253 244 255", // fuchsia-50
      foregroundSecondary: "245 208 254", // fuchsia-200
      foregroundTertiary: "232 170 251", // fuchsia-300
      foregroundMuted: "217 130 247", // fuchsia-400
      border: "112 26 166",
      borderSecondary: "147 51 190",
      primary: "232 121 249", // fuchsia-400
      primaryForeground: "59 7 100",
      accent: "112 26 166",
      accentForeground: "253 244 255",
      card: "86 10 140",
      cardForeground: "253 244 255",
      sidebarBackground: "59 7 100",
      sidebarForeground: "253 244 255",
      sidebarBorder: "112 26 166",
      sidebarAccent: "86 10 140",
      hover: "112 26 166",
      hoverForeground: "253 244 255",
      rowHover: "86 10 140",
      muted: "112 26 166",
      mutedForeground: "217 130 247",
      tableHeader: "86 10 140",
      tableHeaderForeground: "217 130 247",
      tableRowHover: "86 10 140",
      tableBorder: "112 26 166",
      link: "232 121 249",
      linkHover: "232 121 249",
      input: "112 26 166",
      ring: "232 121 249",
      destructive: "248 113 113",
      destructiveForeground: "59 7 100",
    },
    isBuiltIn: true,
  },
  {
    id: "sunset",
    name: "Sunset",
    baseMode: "light",
    colors: {
      background: "255 251 245", // warm white
      backgroundSecondary: "255 247 237", // orange-50
      backgroundTertiary: "254 243 199", // amber-100
      foreground: "67 20 7", // warm dark brown
      foregroundSecondary: "120 53 15", // amber-800
      foregroundTertiary: "161 98 7", // amber-600
      foregroundMuted: "161 98 7",
      border: "253 230 138", // amber-200
      borderSecondary: "252 211 77", // amber-300
      primary: "234 88 12", // orange-600
      primaryForeground: "255 255 255",
      accent: "254 243 199",
      accentForeground: "67 20 7",
      card: "255 251 245",
      cardForeground: "67 20 7",
      sidebarBackground: "255 247 237",
      sidebarForeground: "67 20 7",
      sidebarBorder: "253 230 138",
      sidebarAccent: "254 243 199",
      hover: "254 243 199",
      hoverForeground: "67 20 7",
      rowHover: "255 247 237",
      muted: "254 243 199",
      mutedForeground: "161 98 7",
      tableHeader: "255 247 237",
      tableHeaderForeground: "161 98 7",
      tableRowHover: "255 247 237",
      tableBorder: "253 230 138",
      link: "234 88 12",
      linkHover: "234 88 12",
      input: "253 230 138",
      ring: "234 88 12",
      destructive: "239 68 68",
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
  },
  {
    id: "sky",
    name: "Sky",
    baseMode: "light",
    colors: {
      background: "240 249 255", // sky-50
      backgroundSecondary: "224 242 254", // sky-100
      backgroundTertiary: "186 230 253", // sky-200
      foreground: "12 74 110", // sky-800
      foregroundSecondary: "3 105 161", // sky-700
      foregroundTertiary: "14 165 233", // sky-500
      foregroundMuted: "56 189 248", // sky-400
      border: "186 230 253", // sky-200
      borderSecondary: "125 211 252", // sky-300
      primary: "2 132 199", // sky-600
      primaryForeground: "255 255 255",
      accent: "186 230 253",
      accentForeground: "12 74 110",
      card: "240 249 255",
      cardForeground: "12 74 110",
      sidebarBackground: "224 242 254",
      sidebarForeground: "12 74 110",
      sidebarBorder: "186 230 253",
      sidebarAccent: "186 230 253",
      hover: "186 230 253",
      hoverForeground: "12 74 110",
      rowHover: "224 242 254",
      muted: "186 230 253",
      mutedForeground: "56 189 248",
      tableHeader: "224 242 254",
      tableHeaderForeground: "56 189 248",
      tableRowHover: "224 242 254",
      tableBorder: "186 230 253",
      link: "2 132 199",
      linkHover: "2 132 199",
      input: "186 230 253",
      ring: "2 132 199",
      destructive: "239 68 68",
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
  },
  {
    id: "sea-glass",
    name: "Sea Glass",
    baseMode: "light",
    colors: {
      background: "240 253 250", // teal-50
      backgroundSecondary: "204 251 241", // teal-100
      backgroundTertiary: "153 246 228", // teal-200
      foreground: "17 94 89", // teal-800
      foregroundSecondary: "15 118 110", // teal-700
      foregroundTertiary: "20 184 166", // teal-500
      foregroundMuted: "45 212 191", // teal-400
      border: "153 246 228", // teal-200
      borderSecondary: "94 234 212", // teal-300
      primary: "13 148 136", // teal-600
      primaryForeground: "255 255 255",
      accent: "153 246 228",
      accentForeground: "17 94 89",
      card: "240 253 250",
      cardForeground: "17 94 89",
      sidebarBackground: "204 251 241",
      sidebarForeground: "17 94 89",
      sidebarBorder: "153 246 228",
      sidebarAccent: "153 246 228",
      hover: "153 246 228",
      hoverForeground: "17 94 89",
      rowHover: "204 251 241",
      muted: "153 246 228",
      mutedForeground: "45 212 191",
      tableHeader: "204 251 241",
      tableHeaderForeground: "45 212 191",
      tableRowHover: "204 251 241",
      tableBorder: "153 246 228",
      link: "13 148 136",
      linkHover: "13 148 136",
      input: "153 246 228",
      ring: "13 148 136",
      destructive: "239 68 68",
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
  },

  {
    id: "beach",
    name: "Beach",
    baseMode: "light",
    colors: {
      background: "247 253 253", // pale sea foam white
      backgroundSecondary: "232 248 247", // site's aqua tint
      backgroundTertiary: "210 240 242", // shallow water
      foreground: "16 38 43", // deep sea ink
      foregroundSecondary: "53 84 92",
      foregroundTertiary: "70 105 113",
      foregroundMuted: "70 105 113",
      border: "199 232 234",
      borderSecondary: "0 186 216", // brand cyan
      primary: "0 121 142", // deepened brand cyan, AA on page
      primaryForeground: "255 255 255",
      accent: "210 240 242",
      accentForeground: "16 38 43",
      card: "255 255 255",
      cardForeground: "16 38 43",
      sidebarBackground: "232 248 247",
      sidebarForeground: "16 38 43",
      sidebarBorder: "199 232 234",
      sidebarAccent: "210 240 242",
      hover: "210 240 242",
      hoverForeground: "16 38 43",
      rowHover: "232 248 247",
      muted: "210 240 242",
      mutedForeground: "70 105 113",
      tableHeader: "232 248 247",
      tableHeaderForeground: "70 105 113",
      tableRowHover: "232 248 247",
      tableBorder: "199 232 234",
      link: "0 121 142",
      linkHover: "0 95 112",
      input: "199 232 234",
      ring: "0 121 142",
      destructive: "239 68 68",
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
  },

  // === Additional themes (hidden under "see more") ===
  {
    id: "choco-mint",
    name: "Choco Mint",
    baseMode: "dark",
    colors: {
      background: "41 28 23", // dark chocolate
      backgroundSecondary: "56 40 32", // milk chocolate
      backgroundTertiary: "74 54 43", // lighter chocolate
      foreground: "209 250 229", // mint green (emerald-100)
      foregroundSecondary: "167 243 208", // emerald-200
      foregroundTertiary: "110 231 183", // emerald-300
      foregroundMuted: "110 231 183",
      border: "74 54 43",
      borderSecondary: "92 68 55",
      primary: "52 211 153", // emerald-400
      primaryForeground: "41 28 23",
      accent: "74 54 43",
      accentForeground: "209 250 229",
      card: "56 40 32",
      cardForeground: "209 250 229",
      sidebarBackground: "41 28 23",
      sidebarForeground: "209 250 229",
      sidebarBorder: "74 54 43",
      sidebarAccent: "56 40 32",
      hover: "74 54 43",
      hoverForeground: "209 250 229",
      rowHover: "56 40 32",
      muted: "74 54 43",
      mutedForeground: "110 231 183",
      tableHeader: "56 40 32",
      tableHeaderForeground: "110 231 183",
      tableRowHover: "56 40 32",
      tableBorder: "74 54 43",
      link: "52 211 153",
      linkHover: "52 211 153",
      input: "74 54 43",
      ring: "52 211 153",
      destructive: "248 113 113",
      destructiveForeground: "41 28 23",
    },
    isBuiltIn: true,
    hidden: true,
  },
  {
    id: "aubergine",
    name: "Aubergine",
    baseMode: "dark",
    colors: {
      background: "61 28 79", // deep purple (Slack-inspired)
      backgroundSecondary: "78 36 101", // lighter purple
      backgroundTertiary: "97 45 126", // even lighter
      foreground: "255 255 255", // white
      foregroundSecondary: "225 213 231", // soft purple-white
      foregroundTertiary: "195 171 206", // muted purple
      foregroundMuted: "195 171 206",
      border: "97 45 126",
      borderSecondary: "116 54 151",
      primary: "74 212 183", // teal accent (Slack-style)
      primaryForeground: "61 28 79",
      accent: "97 45 126",
      accentForeground: "255 255 255",
      card: "78 36 101",
      cardForeground: "255 255 255",
      sidebarBackground: "61 28 79",
      sidebarForeground: "255 255 255",
      sidebarBorder: "97 45 126",
      sidebarAccent: "78 36 101",
      hover: "97 45 126",
      hoverForeground: "255 255 255",
      rowHover: "78 36 101",
      muted: "97 45 126",
      mutedForeground: "195 171 206",
      tableHeader: "78 36 101",
      tableHeaderForeground: "195 171 206",
      tableRowHover: "78 36 101",
      tableBorder: "97 45 126",
      link: "74 212 183",
      linkHover: "74 212 183",
      input: "97 45 126",
      ring: "74 212 183",
      destructive: "248 113 113",
      destructiveForeground: "61 28 79",
    },
    isBuiltIn: true,
    hidden: true,
  },
  {
    id: "cmyk",
    name: "CMYK",
    baseMode: "light",
    colors: {
      background: "255 255 255", // white (paper)
      backgroundSecondary: "250 250 250", // off-white
      backgroundTertiary: "245 245 220", // cream/yellow tint
      foreground: "20 20 20", // rich black (K)
      foregroundSecondary: "60 60 60",
      foregroundTertiary: "100 100 100",
      foregroundMuted: "120 120 120",
      border: "0 188 212", // cyan border
      borderSecondary: "236 64 122", // magenta
      primary: "0 188 212", // cyan (C)
      primaryForeground: "255 255 255",
      accent: "255 235 59", // yellow (Y)
      accentForeground: "20 20 20",
      card: "255 255 255",
      cardForeground: "20 20 20",
      sidebarBackground: "236 64 122", // magenta sidebar
      sidebarForeground: "255 255 255",
      sidebarBorder: "194 24 91", // darker magenta
      sidebarAccent: "255 235 59", // yellow accent
      hover: "224 247 250", // light cyan
      hoverForeground: "20 20 20",
      rowHover: "250 250 250",
      muted: "245 245 220",
      mutedForeground: "120 120 120",
      tableHeader: "250 250 250",
      tableHeaderForeground: "120 120 120",
      tableRowHover: "250 250 250",
      tableBorder: "0 188 212",
      link: "0 188 212",
      linkHover: "0 188 212",
      input: "0 188 212",
      ring: "0 188 212",
      destructive: "239 68 68",
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
    hidden: true,
  },
  {
    id: "bubblegum",
    name: "Bubblegum",
    baseMode: "light",
    colors: {
      background: "255 250 253", // pink-tinted white
      backgroundSecondary: "253 242 248", // soft pink
      backgroundTertiary: "251 207 232", // pink-200
      foreground: "131 24 67", // pink-900
      foregroundSecondary: "157 23 77", // pink-800
      foregroundTertiary: "219 39 119", // pink-600
      foregroundMuted: "236 72 153", // pink-500
      border: "251 207 232", // pink-200
      borderSecondary: "249 168 212", // pink-300
      primary: "236 72 153", // pink-500
      primaryForeground: "255 255 255",
      accent: "251 207 232",
      accentForeground: "131 24 67",
      card: "255 250 253",
      cardForeground: "131 24 67",
      sidebarBackground: "253 242 248",
      sidebarForeground: "131 24 67",
      sidebarBorder: "251 207 232",
      sidebarAccent: "251 207 232",
      hover: "251 207 232",
      hoverForeground: "131 24 67",
      rowHover: "253 242 248",
      muted: "251 207 232",
      mutedForeground: "236 72 153",
      tableHeader: "253 242 248",
      tableHeaderForeground: "236 72 153",
      tableRowHover: "253 242 248",
      tableBorder: "251 207 232",
      link: "236 72 153",
      linkHover: "236 72 153",
      input: "251 207 232",
      ring: "236 72 153",
      destructive: "239 68 68",
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
    hidden: true,
  },
  {
    id: "forest",
    name: "Forest",
    baseMode: "dark",
    colors: {
      background: "20 26 25", // custom dark green
      backgroundSecondary: "30 41 38",
      backgroundTertiary: "45 60 55",
      foreground: "236 253 245", // emerald-50
      foregroundSecondary: "167 243 208", // emerald-200
      foregroundTertiary: "110 231 183", // emerald-300
      foregroundMuted: "110 231 183",
      border: "45 60 55",
      borderSecondary: "64 80 74",
      primary: "52 211 153", // emerald-400
      primaryForeground: "20 26 25",
      accent: "45 60 55",
      accentForeground: "236 253 245",
      card: "30 41 38",
      cardForeground: "236 253 245",
      sidebarBackground: "20 26 25",
      sidebarForeground: "236 253 245",
      sidebarBorder: "45 60 55",
      sidebarAccent: "30 41 38",
      hover: "45 60 55",
      hoverForeground: "236 253 245",
      rowHover: "30 41 38",
      muted: "45 60 55",
      mutedForeground: "110 231 183",
      tableHeader: "30 41 38",
      tableHeaderForeground: "110 231 183",
      tableRowHover: "30 41 38",
      tableBorder: "45 60 55",
      link: "52 211 153",
      linkHover: "52 211 153",
      input: "45 60 55",
      ring: "52 211 153",
      destructive: "248 113 113",
      destructiveForeground: "20 26 25",
    },
    isBuiltIn: true,
    hidden: true,
  },
  {
    id: "dracula",
    name: "Dracula",
    baseMode: "dark",
    colors: {
      background: "40 42 54", // Dracula background
      backgroundSecondary: "68 71 90", // Dracula current line
      backgroundTertiary: "82 86 110", // between current line and comment
      foreground: "248 248 242", // Dracula foreground
      foregroundSecondary: "189 147 249", // Dracula purple
      foregroundTertiary: "204 210 232", // lightened comment, readable on every surface
      foregroundMuted: "204 210 232",
      border: "68 71 90",
      borderSecondary: "98 114 164",
      primary: "139 233 253", // Dracula cyan
      primaryForeground: "40 42 54",
      accent: "68 71 90",
      accentForeground: "248 248 242",
      card: "68 71 90",
      cardForeground: "248 248 242",
      sidebarBackground: "40 42 54",
      sidebarForeground: "248 248 242",
      sidebarBorder: "68 71 90",
      sidebarAccent: "68 71 90",
      hover: "68 71 90",
      hoverForeground: "248 248 242",
      rowHover: "68 71 90",
      // Kept one step lighter than `card`/`tableHeader` so bare `bg-muted`
      // blocks (skeletons, dividers, slider tracks) stay visible on a card.
      muted: "82 86 110",
      mutedForeground: "204 210 232",
      tableHeader: "68 71 90",
      tableHeaderForeground: "204 210 232",
      tableRowHover: "68 71 90",
      tableBorder: "68 71 90",
      link: "139 233 253",
      linkHover: "139 233 253",
      input: "68 71 90",
      ring: "139 233 253",
      destructive: "248 113 113",
      destructiveForeground: "40 42 54",
    },
    isBuiltIn: true,
    hidden: true,
  },
  {
    id: "nord",
    name: "Nord",
    baseMode: "dark",
    colors: {
      background: "26 28 34", // nord0
      backgroundSecondary: "32 34 40", // nord1
      backgroundTertiary: "67 76 94", // nord2
      foreground: "236 239 244", // nord6
      foregroundSecondary: "229 233 240", // nord5
      foregroundTertiary: "216 222 233", // nord4
      foregroundMuted: "216 222 233",
      border: "67 76 94",
      borderSecondary: "76 86 106", // nord3
      primary: "136 192 208", // nord8
      primaryForeground: "46 52 64",
      accent: "67 76 94",
      accentForeground: "236 239 244",
      card: "59 66 82",
      cardForeground: "236 239 244",
      sidebarBackground: "46 52 64",
      sidebarForeground: "236 239 244",
      sidebarBorder: "67 76 94",
      sidebarAccent: "59 66 82",
      hover: "67 76 94",
      hoverForeground: "236 239 244",
      rowHover: "59 66 82",
      muted: "67 76 94",
      mutedForeground: "216 222 233",
      tableHeader: "32 34 40",
      tableHeaderForeground: "216 222 233",
      tableRowHover: "32 34 40",
      tableBorder: "67 76 94",
      link: "136 192 208",
      linkHover: "136 192 208",
      input: "67 76 94",
      ring: "136 192 208",
      destructive: "248 113 113",
      destructiveForeground: "26 28 34",
    },
    isBuiltIn: true,
    hidden: true,
  },
  {
    id: "midnight",
    name: "Midnight",
    baseMode: "dark",
    colors: {
      background: "3 7 18", // very dark blue-black
      backgroundSecondary: "15 23 42", // slate-900
      backgroundTertiary: "30 41 59", // slate-800
      foreground: "248 250 252", // slate-50
      foregroundSecondary: "226 232 240", // slate-200
      foregroundTertiary: "148 163 184", // slate-400
      foregroundMuted: "148 163 184",
      border: "30 41 59",
      borderSecondary: "51 65 85",
      primary: "99 102 241", // indigo-500
      primaryForeground: "255 255 255",
      accent: "30 41 59",
      accentForeground: "248 250 252",
      card: "15 23 42",
      cardForeground: "248 250 252",
      sidebarBackground: "3 7 18",
      sidebarForeground: "248 250 252",
      sidebarBorder: "30 41 59",
      sidebarAccent: "15 23 42",
      hover: "30 41 59",
      hoverForeground: "248 250 252",
      rowHover: "15 23 42",
      muted: "30 41 59",
      mutedForeground: "148 163 184",
      tableHeader: "15 23 42",
      tableHeaderForeground: "148 163 184",
      tableRowHover: "15 23 42",
      tableBorder: "30 41 59",
      link: "99 102 241",
      linkHover: "99 102 241",
      input: "30 41 59",
      ring: "99 102 241",
      destructive: "248 113 113",
      destructiveForeground: "3 7 18",
    },
    isBuiltIn: true,
    hidden: true,
  },
  // === Catppuccin (https://catppuccin.com) ===
  // Latte is Catppuccin's only light flavour; Mocha is the darkest.
  {
    id: "catppuccin-latte",
    name: "Catppuccin Latte",
    baseMode: "light",
    colors: {
      background: "239 241 245", // Base
      backgroundSecondary: "230 233 239", // Mantle
      backgroundTertiary: "220 224 232", // Crust
      foreground: "76 79 105", // Text
      foregroundSecondary: "92 95 119", // Subtext1
      foregroundTertiary: "92 95 119", // Subtext1 — Subtext0 lands at 4.4:1 on Base, just under AA
      foregroundMuted: "92 95 119",
      border: "204 208 218", // Surface0
      borderSecondary: "188 192 204", // Surface1
      primary: "136 57 239", // Mauve
      primaryForeground: "255 255 255",
      accent: "220 224 232",
      accentForeground: "76 79 105",
      card: "239 241 245",
      cardForeground: "76 79 105",
      sidebarBackground: "230 233 239", // Mantle
      sidebarForeground: "76 79 105",
      sidebarBorder: "204 208 218",
      sidebarAccent: "220 224 232",
      hover: "220 224 232",
      hoverForeground: "76 79 105",
      rowHover: "230 233 239",
      muted: "220 224 232",
      mutedForeground: "92 95 119",
      tableHeader: "230 233 239",
      tableHeaderForeground: "92 95 119",
      tableRowHover: "230 233 239",
      tableBorder: "204 208 218",
      link: "136 57 239",
      linkHover: "136 57 239",
      input: "204 208 218",
      ring: "136 57 239",
      destructive: "210 15 57", // Red
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
    hidden: true,
  },
  {
    id: "catppuccin-mocha",
    name: "Catppuccin Mocha",
    baseMode: "dark",
    colors: {
      background: "24 24 37", // Mantle
      backgroundSecondary: "30 30 46", // Base
      backgroundTertiary: "49 50 68", // Surface0
      foreground: "205 214 244", // Text
      foregroundSecondary: "186 194 222", // Subtext1
      foregroundTertiary: "166 173 200", // Subtext0
      foregroundMuted: "166 173 200",
      border: "49 50 68", // Surface0
      borderSecondary: "69 71 90", // Surface1
      primary: "203 166 247", // Mauve
      primaryForeground: "30 30 46",
      accent: "49 50 68",
      accentForeground: "205 214 244",
      card: "30 30 46", // Base, one step up from the Mantle page
      cardForeground: "205 214 244",
      sidebarBackground: "17 17 27", // Crust
      sidebarForeground: "205 214 244",
      sidebarBorder: "49 50 68",
      sidebarAccent: "30 30 46",
      hover: "49 50 68",
      hoverForeground: "205 214 244",
      rowHover: "49 50 68",
      muted: "49 50 68", // kept a step lighter than `card` so bare bg-muted blocks stay visible
      mutedForeground: "166 173 200",
      tableHeader: "49 50 68",
      tableHeaderForeground: "166 173 200",
      tableRowHover: "49 50 68",
      tableBorder: "49 50 68",
      link: "137 180 250", // Blue
      linkHover: "180 190 254", // Lavender
      input: "69 71 90",
      ring: "203 166 247",
      destructive: "243 139 168", // Red
      destructiveForeground: "30 30 46",
    },
    isBuiltIn: true,
    hidden: true,
  },
  // === Tokyo Night (https://github.com/folke/tokyonight.nvim) ===
  {
    id: "tokyo-night",
    name: "Tokyo Night",
    baseMode: "dark",
    colors: {
      background: "26 27 38", // bg
      backgroundSecondary: "31 35 53", // bg_float
      backgroundTertiary: "41 46 66", // bg_highlight
      foreground: "192 202 245", // fg
      foregroundSecondary: "169 177 214", // fg_dark
      foregroundTertiary: "154 165 206", // comment is only 3:1 here, so use the lighter blue-grey
      foregroundMuted: "154 165 206",
      border: "41 46 66", // bg_highlight
      borderSecondary: "59 66 97", // fg_gutter
      primary: "122 162 247", // blue
      primaryForeground: "26 27 38",
      accent: "41 46 66",
      accentForeground: "192 202 245",
      card: "31 35 53",
      cardForeground: "192 202 245",
      sidebarBackground: "22 22 30", // bg_dark
      sidebarForeground: "192 202 245",
      sidebarBorder: "41 46 66",
      sidebarAccent: "31 35 53",
      hover: "41 46 66",
      hoverForeground: "192 202 245",
      rowHover: "31 35 53",
      muted: "41 46 66", // a step lighter than `card` so bare bg-muted blocks stay visible
      mutedForeground: "154 165 206",
      tableHeader: "31 35 53",
      tableHeaderForeground: "154 165 206",
      tableRowHover: "31 35 53",
      tableBorder: "41 46 66",
      link: "122 162 247",
      linkHover: "125 207 255", // cyan
      input: "41 46 66",
      ring: "122 162 247",
      destructive: "247 118 142", // red
      destructiveForeground: "26 27 38",
    },
    isBuiltIn: true,
    hidden: true,
  },
  {
    id: "tokyo-night-day",
    name: "Tokyo Night Day",
    baseMode: "light",
    colors: {
      background: "225 226 231", // bg
      backgroundSecondary: "208 213 227", // bg_dark
      backgroundTertiary: "196 200 218", // bg_highlight
      foreground: "52 59 88", // the family's dark navy; Day's own blue fg only clears AA on the page itself
      foregroundSecondary: "86 95 137", // comment
      foregroundTertiary: "79 88 120", // comment darkened to stay readable on `muted`
      foregroundMuted: "79 88 120",
      border: "196 200 218", // bg_highlight
      borderSecondary: "168 174 203", // fg_gutter
      primary: "55 96 191", // fg (Day's signature blue)
      primaryForeground: "255 255 255",
      accent: "208 213 227",
      accentForeground: "52 59 88",
      card: "225 226 231",
      cardForeground: "52 59 88",
      sidebarBackground: "208 213 227",
      sidebarForeground: "52 59 88",
      sidebarBorder: "196 200 218",
      sidebarAccent: "196 200 218",
      hover: "208 213 227",
      hoverForeground: "52 59 88",
      rowHover: "208 213 227",
      muted: "208 213 227",
      mutedForeground: "79 88 120",
      tableHeader: "208 213 227",
      tableHeaderForeground: "79 88 120",
      tableRowHover: "208 213 227",
      tableBorder: "196 200 218",
      link: "55 96 191",
      linkHover: "55 96 191",
      input: "196 200 218",
      ring: "55 96 191",
      destructive: "198 67 67", // red1; Day's brighter red cannot carry white text
      destructiveForeground: "255 255 255",
    },
    isBuiltIn: true,
    hidden: true,
  },
];

/**
 * Convert an RGB string to hex format
 * e.g., "255 255 255" -> "#ffffff"
 */
export function rgbToHex(rgb: string): string {
  const [r, g, b] = rgb.split(" ").map(Number);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * Get a theme by ID
 */
export function getThemeById(id: string): CustomTheme | undefined {
  return presetThemes.find((theme) => theme.id === id);
}

/**
 * CSS variable name mappings for theme colors
 */
/**
 * Tenant-customizable brand colors. Expand into the full ThemeColors shape
 * via deriveThemeFromBrand(); text and secondary accents are fixed.
 */
export interface BrandColors {
  /** Main brand/CTA color (buttons, links, accents) */
  primary: string;
  /** Page background color */
  background: string;
}

export const DEFAULT_BRAND_COLORS: BrandColors = {
  primary: "#4a5d4e",
  background: "#faf6f0",
};

// Fixed palette for non-customizable surfaces. Hex kept here so
// deriveThemeFromBrand stays self-contained.
const BRAND_SECONDARY_HEX = "#c5a37e";
const BRAND_TEXT_HEX = "#1b211b";

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(hex: string): Rgb {
  const clean = hex.replace("#", "").trim();
  if (!/^(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(clean)) {
    return { r: 0, g: 0, b: 0 };
  }
  const expanded =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const n = parseInt(expanded, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

function toRgbString({ r, g, b }: Rgb): string {
  return `${Math.round(r)} ${Math.round(g)} ${Math.round(b)}`;
}

/** Weighted mix: t=0 returns a, t=1 returns b. */
function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

/** Perceived luminance 0..1 using sRGB coefficients. */
function luminance({ r, g, b }: Rgb): number {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function contrastText(bg: Rgb, lightText: Rgb, darkText: Rgb): Rgb {
  return luminance(bg) > 0.55 ? darkText : lightText;
}

/** Shift toward white (positive) or black (negative) by a fraction. */
function shift(c: Rgb, t: number): Rgb {
  return t >= 0
    ? mix(c, { r: 255, g: 255, b: 255 }, t)
    : mix(c, { r: 0, g: 0, b: 0 }, -t);
}

/**
 * Derive the full ThemeColors shape from the tenant's brand and page colors.
 *
 * Uses fixed values for text and secondary accents; all surrounding shades
 * (borders, hovers, muted text, tables, sidebar) are computed through
 * deterministic mixes. This is what the ThemeProvider consumes.
 */
export function deriveThemeFromBrand(brand: BrandColors): {
  colors: ThemeColors;
  baseMode: "light" | "dark";
} {
  const primary = parseHex(brand.primary);
  const secondary = parseHex(BRAND_SECONDARY_HEX);
  const bg = parseHex(brand.background);
  const text = parseHex(BRAND_TEXT_HEX);
  const white: Rgb = { r: 255, g: 255, b: 255 };

  const isLight = luminance(bg) > 0.5;
  // For derived shades, "toward contrast" means darker on light bg, lighter on dark bg.
  const darken = (c: Rgb, t: number): Rgb =>
    isLight ? shift(c, -t) : shift(c, t);

  const colors: ThemeColors = {
    background: toRgbString(bg),
    backgroundSecondary: toRgbString(darken(bg, 0.03)),
    backgroundTertiary: toRgbString(darken(bg, 0.07)),

    foreground: toRgbString(text),
    foregroundSecondary: toRgbString(mix(text, bg, 0.25)),
    foregroundTertiary: toRgbString(mix(text, bg, 0.5)),
    foregroundMuted: toRgbString(mix(text, bg, 0.6)),

    border: toRgbString(mix(bg, text, 0.12)),
    borderSecondary: toRgbString(secondary),

    primary: toRgbString(primary),
    primaryForeground: toRgbString(
      contrastText(primary, white, { r: 0, g: 0, b: 0 }),
    ),

    accent: toRgbString(mix(secondary, bg, 0.7)),
    accentForeground: toRgbString(text),

    card: toRgbString(bg),
    cardForeground: toRgbString(text),

    muted: toRgbString(darken(bg, 0.07)),
    mutedForeground: toRgbString(mix(text, bg, 0.5)),

    sidebarBackground: toRgbString(darken(bg, 0.03)),
    sidebarForeground: toRgbString(text),
    sidebarBorder: toRgbString(mix(bg, text, 0.12)),
    sidebarAccent: toRgbString(darken(bg, 0.07)),

    hover: toRgbString(darken(bg, 0.07)),
    hoverForeground: toRgbString(text),
    rowHover: toRgbString(darken(bg, 0.03)),

    tableHeader: toRgbString(darken(bg, 0.03)),
    tableHeaderForeground: toRgbString(mix(text, bg, 0.5)),
    tableRowHover: toRgbString(darken(bg, 0.03)),
    tableBorder: toRgbString(mix(bg, text, 0.12)),

    link: toRgbString(primary),
    linkHover: toRgbString(shift(primary, isLight ? -0.15 : 0.15)),

    input: toRgbString(mix(bg, text, 0.12)),
    ring: toRgbString(primary),

    // Destructive stays semantic — red regardless of brand.
    destructive: isLight ? "239 68 68" : "248 113 113",
    destructiveForeground: isLight ? "255 255 255" : toRgbString(bg),
  };

  return { colors, baseMode: isLight ? "light" : "dark" };
}

/**
 * WCAG relative luminance (sRGB gamma-corrected).
 * See https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
function srgbToLinear(c: number): number {
  const cs = c / 255;
  return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
}

function relativeLuminance({ r, g, b }: Rgb): number {
  return (
    0.2126 * srgbToLinear(r) +
    0.7152 * srgbToLinear(g) +
    0.0722 * srgbToLinear(b)
  );
}

/** WCAG contrast ratio between two colors (1 to 21). */
export function contrastRatio(aHex: string, bHex: string): number {
  const la = relativeLuminance(parseHex(aHex));
  const lb = relativeLuminance(parseHex(bHex));
  const [L1, L2] = la > lb ? [la, lb] : [lb, la];
  return (L1 + 0.05) / (L2 + 0.05);
}

export interface ContrastIssue {
  /** User-facing label for the failing pair. */
  pair: string;
  /** Short explanation of where the pair is visible. */
  detail: string;
  /** Measured ratio. */
  ratio: number;
  /** WCAG threshold the pair is expected to meet. */
  threshold: number;
  severity: "warning" | "error";
}

/**
 * Flag brand color pairs that fail WCAG contrast minimums.
 * - Body text (fixed) vs page: 4.5:1 (AA normal text)
 * - Primary (buttons) vs page: 3:1 (AA non-text / UI components)
 * - Primary (links) vs page: 4.5:1 (AA normal text)
 */
export function getBrandContrastIssues(brand: BrandColors): ContrastIssue[] {
  const issues: ContrastIssue[] = [];

  const textBg = contrastRatio(BRAND_TEXT_HEX, brand.background);
  if (textBg < 4.5) {
    issues.push({
      pair: "Main Text on Page Color",
      detail: "Body text needs 4.5:1 contrast to stay readable.",
      ratio: textBg,
      threshold: 4.5,
      severity: textBg < 3 ? "error" : "warning",
    });
  }

  const primaryBg = contrastRatio(brand.primary, brand.background);
  if (primaryBg < 3) {
    issues.push({
      pair: "Brand Color on Page Color",
      detail: "Buttons need 3:1 contrast against the page.",
      ratio: primaryBg,
      threshold: 3,
      severity: primaryBg < 2 ? "error" : "warning",
    });
  } else if (primaryBg < 4.5) {
    issues.push({
      pair: "Link Color on Page Color",
      detail:
        "Link text needs 4.5:1 contrast against the page to stay readable.",
      ratio: primaryBg,
      threshold: 4.5,
      severity: "warning",
    });
  }

  return issues;
}

export const colorToCssVar: Record<keyof ThemeColors, string> = {
  background: "--background",
  backgroundSecondary: "--background-secondary",
  backgroundTertiary: "--background-tertiary",
  foreground: "--foreground",
  foregroundSecondary: "--foreground-secondary",
  foregroundTertiary: "--foreground-tertiary",
  foregroundMuted: "--foreground-muted",
  border: "--border",
  borderSecondary: "--border-secondary",
  primary: "--primary",
  primaryForeground: "--primary-foreground",
  accent: "--accent",
  accentForeground: "--accent-foreground",
  card: "--card",
  cardForeground: "--card-foreground",
  sidebarBackground: "--sidebar-background",
  sidebarForeground: "--sidebar-foreground",
  sidebarBorder: "--sidebar-border",
  sidebarAccent: "--sidebar-accent",
  muted: "--muted",
  mutedForeground: "--muted-foreground",
  hover: "--hover",
  hoverForeground: "--hover-foreground",
  rowHover: "--row-hover",
  tableHeader: "--table-header",
  tableHeaderForeground: "--table-header-foreground",
  tableRowHover: "--table-row-hover",
  tableBorder: "--table-border",
  link: "--link",
  linkHover: "--link-hover",
  input: "--input",
  ring: "--ring",
  destructive: "--destructive",
  destructiveForeground: "--destructive-foreground",
};
